/**
 * The landing page.
 *
 * It leads with the refusal, not with the autofill. Everyone selling this
 * category leads with speed; the reason to buy this one is that it will not
 * write an answer the customer cannot defend, and saying so first qualifies the
 * buyer in one sentence.
 */

import { link } from '../router';
import { Button } from '../ui';
import {
  ArrowRightIcon,
  BookOpenIcon,
  GlobeIcon,
  Icon,
  IconHover,
  QuoteIcon,
  XIcon,
} from '../icons';
import { SiteFooter, SiteHeader } from './Chrome';

const PILLARS = [
  {
    icon: BookOpenIcon,
    title: 'A bank of what you can prove',
    body: 'Every claim is a card with its source and the date a human last reviewed it. A card nobody has looked at in a year stops being citable — silently answering from a stale policy is how a questionnaire becomes a misrepresentation.',
  },
  {
    icon: QuoteIcon,
    title: 'Autofill that cites, or refuses',
    body: 'Drop in the spreadsheet a customer sent. Every answer comes back with a link to the evidence behind it. Anything unbacked comes back marked in red with the text discarded, not greyed out for someone to paste at 11pm.',
  },
  {
    icon: GlobeIcon,
    title: 'A trust page that gets there first',
    body: 'Publish the cards you are happy to state in public and answer most of the questionnaire before anyone sends it.',
  },
];

export function LandingView() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="mx-auto w-full max-w-5xl flex-1 px-5">
        <section className="border-b border-line py-20 md:py-28">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-line px-3 py-1 text-[12.5px] text-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            For the eight-person startup that just got its first enterprise questionnaire
          </p>
          <h1 className="max-w-3xl text-[38px] leading-[1.12] md:text-[52px]">
            Answer security questionnaires from evidence you can point at — and never from anything
            else.
          </h1>
          <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-muted">
            Vouchkeep fills in the spreadsheet your customer sent, with a citation on every answer.
            What your evidence does not support comes back refused rather than invented, because an
            answer you cannot defend is worse than a blank cell.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a {...link('/signup')}>
              <Button variant="primary" icon={ArrowRightIcon}>
                Build your evidence bank
              </Button>
            </a>
            <span className="text-[13px] text-faint">
              Bring your own model key · your questionnaire never touches our server
            </span>
          </div>
        </section>

        <section className="grid gap-px border-b border-line bg-line md:grid-cols-3">
          {PILLARS.map((pillar) => (
            <IconHover key={pillar.title} className="bg-bg px-1 py-9 md:px-6">
              <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-accent-wash text-accent">
                <Icon icon={pillar.icon} size={17} />
              </span>
              <h2 className="text-[15px] font-medium">{pillar.title}</h2>
              <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{pillar.body}</p>
            </IconHover>
          ))}
        </section>

        <section className="border-b border-line py-16">
          <h2 className="text-[24px]">What happens to a draft with no evidence</h2>
          <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-muted">
            The rule is enforced in code, not encouraged in the interface. Whatever a model proposes
            passes through one function, and it comes back as a supported answer or as nothing.
          </p>

          <div className="mt-7 overflow-x-auto">
            <table className="w-full min-w-[34rem] border-collapse text-left text-[13.5px]">
              <thead>
                <tr className="border-b border-line text-[12.5px] uppercase tracking-wide text-faint">
                  <th className="py-2.5 pr-4 font-medium">The draft</th>
                  <th className="py-2.5 font-medium">What ships</th>
                </tr>
              </thead>
              <tbody className="text-muted">
                {[
                  ['Cites nothing', 'Refused, text discarded'],
                  ['Cites a card that does not exist', 'Refused, text discarded'],
                  ['Cites evidence past its review date', 'Refused, naming the stale card'],
                  ['Cites fresh evidence in the bank', 'Answered, with its sources'],
                ].map(([draft, result], i) => (
                  <tr key={draft} className={i < 3 ? 'border-b border-line' : ''}>
                    <td className="py-3 pr-4">{draft}</td>
                    <td className="py-3">
                      <span
                        className={
                          result.startsWith('Answered')
                            ? 'chip chip-accent'
                            : 'chip chip-refusal'
                        }
                      >
                        {result}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="grid gap-8 py-16 md:grid-cols-2">
          <div>
            <h2 className="text-[20px]">Where your documents go</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
              The questionnaire is parsed in your browser and never uploaded — only the question text
              reaches us, and only after you have seen which column it came from. Drafting uses your
              own model API key, which is used once per request and never stored, so your internal
              documentation never passes through our provider account.
            </p>
          </div>
          <div className="card p-6">
            <div className="mb-3 flex items-center gap-2 text-[12.5px] uppercase tracking-wide text-faint">
              <Icon icon={XIcon} size={13} isAnimated={false} />
              Deliberately not built
            </div>
            <ul className="space-y-2 text-[13.5px] text-muted">
              <li>Becoming a compliance platform. Vanta does that well.</li>
              <li>Generating policies you have not read.</li>
              <li>Storing a model key on your behalf.</li>
            </ul>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
